import { inject, Injectable } from '@angular/core';
import { ISegmentRule, ISegment } from '../../types/contactModule';
import { nanoid } from 'nanoid';
import {
  filtersToRules,
  IEntitySegmentQuery,
  payloadRules,
} from '../../components/organization/segments/segment-utilities';
import { SecurePersistStateService } from '../storage/local-storage.service';
import { BehaviorSubject } from 'rxjs';

/**
 * The segment builder's working copy: name, description and condition rows,
 * kept while the person moves between the three steps. The segment being
 * edited is also kept in secure storage so a refresh does not lose it.
 */
@Injectable({
  providedIn: 'root',
})
export class SegmentService {
  constructor() {}
  secureStorageService = inject(SecurePersistStateService);

  private segmentName: string = '';
  private description: string = '';
  private segmentRules: ISegmentRule[] = [];
  /** Whose rows the builder holds: a new segment, or a saved one being edited. */
  private mode: 'create' | 'edit' | null = null;

  // selectedSegment for editing purposes
  selectedSegment = new BehaviorSubject<IEntitySegmentQuery | null>(null);

  //all segments
  private segmentList: ISegment[] = [];

  // SEGMENT PROPERTIES
  get segmentInformation(): { segmentName: string; description: string } {
    return {
      segmentName: this.segmentName,
      description: this.description,
    };
  }

  /**
   * The rules as they are saved: complete rows only, numbered in order and
   * typed from their attribute. ContactsService.createSegment and
   * updateSegment build the request from this.
   */
  get allSegmentRules(): ISegmentRule[] {
    return payloadRules(this.segmentRules);
  }

  /** The condition rows as the builder shows them, unfinished ones included. */
  get draftRules(): ISegmentRule[] {
    return this.segmentRules;
  }

  // SEGMENT FUNCTIONALITIES
  // update the segment's information
  updateSegmentInformation(information: { segmentName: string; description: string }) {
    this.segmentName = information?.segmentName ?? '';
    this.description = information?.description ?? '';
  }

  // update segment rules
  updateSegmentRules(segmentRules: ISegmentRule[]) {
    this.segmentRules = (segmentRules ?? []).map((rule) => ({
      ...rule,
      id: rule.id ?? nanoid(),
      values: [...(rule.values ?? [])],
    }));
  }

  /** Copies another segment's rules into the builder ("Start from an existing segment"). */
  handleUseAnExistingSegment(existingSegment: IEntitySegmentQuery): void {
    this.segmentRules = filtersToRules(existingSegment?.entitySegmentQueryFilters);
  }

  /** Loads a saved segment for editing: name, description, rules and the record itself. */
  startEditing(segment: IEntitySegmentQuery): void {
    this.mode = 'edit';
    this.updateSegmentInformation({
      segmentName: segment?.name ?? '',
      description: segment?.description ?? '',
    });
    this.handleUseAnExistingSegment(segment);
    this.updateSelectedSegment(segment);
  }

  /** True when the builder already holds this segment (by uuid) with its rules loaded. */
  isEditing(uuid: string | null | undefined): boolean {
    const current = this.selectedSegment.value ?? this.storedSegment();
    return this.mode === 'edit' && !!uuid && current?.uuid === uuid && !!this.segmentName;
  }

  /** New segment: drop anything left over from editing a saved one. */
  startCreating(): void {
    if (this.mode === 'edit') this.clearSegmentInformation();
    this.mode = 'create';
  }

  // update the selected Segment variable upon chooseing to edit a segment
  updateSelectedSegment(segment: IEntitySegmentQuery) {
    this.secureStorageService.saveSecureData('selectedSegment', segment);
    this.selectedSegment.next(segment);
  }

  // get the selected segment
  get getSelectedSegment() {
    const stored = this.storedSegment();
    if (stored && stored !== this.selectedSegment.value) {
      this.selectedSegment.next(stored);
    }
    return this.selectedSegment.asObservable();
  }

  /** The segment saved for editing, or null when storage is empty or unreadable. */
  private storedSegment(): IEntitySegmentQuery | null {
    try {
      return (this.secureStorageService.getData('selectedSegment') as IEntitySegmentQuery) ?? null;
    } catch {
      return null;
    }
  }

  handleCreateSegment() {
    this.segmentList.push({
      name: this.segmentName,
      description: this.description,
      id: nanoid(),
      rules: this.segmentRules,
    });
  }

  clearSegmentInformation() {
    this.mode = null;
    this.segmentName = '';
    this.description = '';
    this.segmentRules = [];
  }
}
